/**
 * Auth context — manages Supabase session lifecycle.
 *
 * Restores session on mount, listens to auth state changes, and extracts
 * custom JWT claims (role, customer_id, trip_scope) from app_metadata
 * (server-set, cannot be spoofed by client).
 *
 * `profileId` is the row id in public.users for the signed-in person. It is
 * NOT the auth uid: users.id and users.auth_user_id are different columns,
 * and every FK that records "who did this" (site_intel.captured_by_user_id,
 * documents.uploaded_by_user_id, field_trips.lead_technician_id) points at
 * users.id. Writing the auth uid into those columns fails the FK.
 */
import {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
  type ReactNode,
} from 'react';
import type { Session, User } from '@supabase/supabase-js';
import { isRole, type Role } from '@pazportal/types';
import { supabase } from '../lib/supabase';

interface AuthState {
  user: User | null;
  session: Session | null;
  loading: boolean;
  role: Role | null;
  customerId: string | null;
  tripScope: string[] | null;
  /** public.users.id for the signed-in user; null until resolved or when no profile exists. */
  profileId: string | null;
  /** Display name from the users profile, when set. */
  profileName: string | null;
  /** Rejects with the Supabase error when sign-out fails so callers can surface it. */
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthState | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  const [profile, setProfile] = useState<{ authUserId: string; id: string; fullName: string | null } | null>(null);

  useEffect(() => {
    // 1. Restore existing session on mount
    supabase.auth.getSession().then(({ data: { session: s } }: { data: { session: Session | null } }) => {
      setSession(s);
      setLoading(false);
    });

    // 2. Listen for auth state changes (login, logout, token refresh)
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((_event: string, s: Session | null) => {
      setSession(s);
      setLoading(false);
    });

    return () => subscription.unsubscribe();
  }, []);

  // 3. Resolve the public.users profile row for FK-bearing writes.
  const authUserId = session?.user?.id ?? null;
  useEffect(() => {
    if (!authUserId) {
      setProfile(null);
      return;
    }
    let cancelled = false;
    supabase
      .from('users')
      .select('id, full_name')
      .eq('auth_user_id', authUserId)
      .maybeSingle()
      .then(({ data }) => {
        if (cancelled) return;
        setProfile(data ? { authUserId, id: data.id, fullName: data.full_name } : null);
      });
    return () => {
      cancelled = true;
    };
  }, [authUserId]);

  const signOut = useCallback(async () => {
    const { error } = await supabase.auth.signOut();
    if (error) throw error;
    setSession(null);
    // ProtectedRoute handles redirect to /login when session is null
  }, []);

  // Extract custom claims from JWT app_metadata (set server-side, tamper-proof)
  const user = session?.user ?? null;
  const meta = user?.app_metadata ?? {};
  const rawRole = typeof meta.role === 'string' ? meta.role : null;
  const role = rawRole && isRole(rawRole) ? rawRole : null;
  const customerId = typeof meta.customer_id === 'string' ? meta.customer_id : null;
  const tripScope = Array.isArray(meta.trip_scope)
    ? meta.trip_scope.filter((v: unknown): v is string => typeof v === 'string')
    : null;

  const profileForSession = profile && profile.authUserId === authUserId ? profile : null;

  return (
    <AuthContext.Provider
      value={{
        user,
        session,
        loading,
        role,
        customerId,
        tripScope,
        profileId: profileForSession?.id ?? null,
        profileName: profileForSession?.fullName ?? null,
        signOut,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth(): AuthState {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within an AuthProvider');
  return ctx;
}
